import React, { useEffect, useRef, useState } from "react";
import { Bell, CheckCheck } from "lucide-react";
import { Link } from "react-router-dom";
import { api, logDev } from "../api";

const ICONS = {
  new_application: "📬",
  interview_proposed: "📅",
  interview_accepted: "✅",
  interview_cancelled: "⚠️",
  cert_issued: "🏆",
  account_approved: "🎉",
};

function fmtWhen(ts) {
  const d = new Date(ts); const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return d.toLocaleDateString();
}

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const ref = useRef(null);

  const load = async () => {
    try {
      const r = await api.get("/me/notifications");
      setItems(r.data.items || []);
      setUnread(r.data.unread || 0);
    } catch (err) {
      logDev.warn("[notifications] fetch failed:", err?.message || err);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("click", onClick);
    return () => { clearInterval(t); document.removeEventListener("click", onClick); };
  }, []);

  const markAll = async () => {
    await api.post("/me/notifications/read-all");
    load();
  };
  const markOne = async (id) => {
    await api.post(`/me/notifications/${id}/read`);
    load();
  };

  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen(o => !o)} data-testid="notif-bell"
        className="relative p-2 rounded-full hover:bg-paper transition">
        <Bell size={18} />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 bg-signal text-white text-[10px] font-bold min-w-[18px] h-[18px] rounded-full flex items-center justify-center px-1"
            data-testid="notif-unread-badge">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-80 md:w-96 bg-white border border-rule rounded-sm shadow-xl z-50" data-testid="notif-dropdown">
          <div className="flex items-center justify-between px-4 py-3 border-b border-rule">
            <span className="eyebrow">Notifications</span>
            {unread > 0 && (
              <button onClick={markAll} className="text-xs text-signal font-semibold inline-flex items-center gap-1 hover:underline" data-testid="notif-mark-all">
                <CheckCheck size={12} /> Mark all read
              </button>
            )}
          </div>
          <div className="max-h-96 overflow-y-auto">
            {items.length === 0 ? (
              <div className="px-4 py-8 text-center text-muted text-sm" data-testid="notif-empty">
                You&apos;re all caught up.
              </div>
            ) : items.map(n => {
              const inner = (
                <div className={`px-4 py-3 border-b border-rule flex gap-3 hover:bg-paper transition ${n.read ? "" : "bg-signal/5"}`}>
                  <div className="text-lg shrink-0">{ICONS[n.type] || "🔔"}</div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm truncate">{n.title}</div>
                    {n.body && <div className="text-xs text-muted mt-0.5 line-clamp-2">{n.body}</div>}
                    <div className="text-[11px] text-muted mt-1 font-mono">{fmtWhen(n.created_at)}</div>
                  </div>
                  {!n.read && <span className="w-2 h-2 rounded-full bg-signal shrink-0 mt-2" />}
                </div>
              );
              return n.link ? (
                <Link key={n.id} to={n.link} onClick={() => { markOne(n.id); setOpen(false); }}
                  data-testid={`notif-item-${n.id}`}>{inner}</Link>
              ) : (
                <button key={n.id} onClick={() => markOne(n.id)} className="w-full text-left"
                  data-testid={`notif-item-${n.id}`}>{inner}</button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
